import type { IPlaylistItem } from "~/types";
import type { IEvent } from "~/videos/types/events";
import type { IBasicVideo, IVideoAngles, TClippedVideoAngle, TVideoAngleSource } from "~/videos/types/video";
import type { IPixellotPlayerSource, IPixellotPlayerSourceType, IPixellotPlayerVideoClipOptions } from "~~/modules/pixellot-sdk";
import { VIDEO_TYPE } from "@/constants";
import { VIDEO_SOURCE_TYPE } from "~/videos/types/video";

export interface IVideoClippedListItem {
  id: string;
  video_duration: number;
  type: "video-clipped-list-item";
  title: string;
  url: string;
  poster?: string | null;
  event: IEvent | IBasicVideo;
}

/** Full-match clipped UI and API clipped uploads: `upload_source_type === clipped` and multi-clip map present. */
export function isClippedVideo(video: IBasicVideo | IEvent | null | undefined): video is IEvent {
  if (!video || video.type !== VIDEO_TYPE.EVENT) return false;
  if (!video.angles?.clipped?.size) return false;
  return video.upload_source_type === VIDEO_SOURCE_TYPE.CLIPPED;
}

function getAnglesMap(video: IBasicVideo) {
  const type = video.upload_source_type;
  const anglesMap = video.angles?.[type as keyof IVideoAngles];
  if (!anglesMap?.size) return undefined;
  return { type, anglesMap };
}

/**
 * Normalized playable sources for the active upload source type.
 * Continuous angles expose `url` on each entry; clipped angles expose sources under `videos[]`.
 */
export function getAnglesValue(video: IBasicVideo, angleType?: IPixellotPlayerSourceType): TVideoAngleSource[] {
  const ctx = getAnglesMap(video);
  if (!ctx) return [];

  const sources: TVideoAngleSource[] = [];

  for (const [angleKey, angle] of ctx.anglesMap.entries()) {
    if (angleType && angleKey !== angleType) continue;

    if (ctx.type === VIDEO_SOURCE_TYPE.CLIPPED) {
      sources.push(...((angle as TClippedVideoAngle).videos ?? []));
    } else {
      sources.push(angle as TVideoAngleSource);
    }
  }

  return sources.filter(Boolean);
}

/**
 * Default angle for the player when several exist: `primary === true` from the API, else first angle in map order.
 */
export function getPrimaryAngleType(video: IBasicVideo): IPixellotPlayerSourceType | undefined {
  const ctx = getAnglesMap(video);
  if (!ctx) return undefined;

  let fallback: IPixellotPlayerSourceType | undefined;

  for (const [angleKey, angle] of ctx.anglesMap) {
    if (angle.primary) return angleKey;
    if (fallback === undefined) fallback = angleKey;
  }

  return fallback;
}

/**
 * Narrow catalog `event` so one list row maps to one stream: chosen angle is `primary` and exposes a single clip in `videos`.
 * Player code can rely on {@link getPrimaryAngleType} / `getAnglesValue(video, primary)[0]` without extra props.
 */
export function getEventFromAngleSource(parent: IEvent, source: TVideoAngleSource, angleType: IPixellotPlayerSourceType): IEvent {
  const anglesMap = parent.angles?.clipped;
  if (!anglesMap?.size) return parent;

  const nextItems = new Map<IPixellotPlayerSourceType, TClippedVideoAngle>();

  for (const [key, angle] of anglesMap) {
    const isActiveAngle = key === angleType;

    nextItems.set(key, { ...angle, primary: isActiveAngle, videos: isActiveAngle ? [source] : [...(angle.videos ?? [])] });
  }

  return {
    ...parent,
    angles: {
      ...parent.angles,
      clipped: nextItems,
    },
  };
}

/**
 * Narrow a clipped catalog event to the playlist tag’s clip: {@link IPlaylistItem.eventVideoId} matches
 * {@link TVideoAngleSource.id} on the primary angle (API `videos[]` `_id`).
 */
export function resolveClippedEventForPlaylistItem(event: IEvent, tag: IPlaylistItem): IEvent {
  if (!isClippedVideo(event)) return event;

  const primaryKey = getPrimaryAngleType(event);
  if (!primaryKey) return event;

  const clips = getAnglesValue(event, primaryKey);
  const source = clips.find((c) => c.id === tag.itemId) ?? clips[0];
  if (!source) return event;

  return getEventFromAngleSource(event, source, primaryKey);
}

/** One Pixellot `src` entry per clipped camera angle; playing URL/thumb follow the angle marked primary on `video`. */
function buildClippedVideoSources(video: IEvent): IPixellotPlayerSource[] {
  const clippedMap = video.angles.clipped;
  if (!clippedMap?.size) return [];

  const primaryKey = getPrimaryAngleType(video);
  const activeAngle = primaryKey ? getAnglesValue(video, primaryKey)[0] : undefined;
  const activeUrl = activeAngle?.url;

  const sources: IPixellotPlayerSource[] = [];

  for (const [angleType, angle] of clippedMap) {
    const firstClip = angle.videos?.[0];
    const url = angleType === primaryKey && activeUrl ? activeUrl : firstClip?.url;
    if (!url) continue;

    const thumbnails_url = angleType === primaryKey && activeUrl ? activeAngle?.thumbnails_url : firstClip?.thumbnails_url;

    sources.push({ type: angleType, url, thumbnails_url });
  }

  return primaryKey ? movePrimaryAngleToFront(sources, primaryKey) : sources;
}

function movePrimaryAngleToFront(sources: TVideoAngleSource[], primaryKey: IPixellotPlayerSourceType) {
  const idx = sources.findIndex((s) => s.type === primaryKey);
  if (idx <= 0) return sources;

  return [sources[idx], ...sources.slice(0, idx), ...sources.slice(idx + 1)];
}

/**
 * Pixellot player `src` list: continuous uploads → flat angles with optional playlist `clip`;
 * clipped video → one entry per camera with the primary angle’s playing URL and others’ first clip.
 */
export function buildVideoPlayerSources(video: IBasicVideo, clip?: IPixellotPlayerVideoClipOptions): IPixellotPlayerSource[] {
  if (isClippedVideo(video)) {
    const sources = buildClippedVideoSources(video);
    if (!clip) return sources;
    return sources.map((s) => ({ ...s, clip }));
  }

  let sources = getAnglesValue(video);
  if (!sources.length) return [];

  const primaryKey = getPrimaryAngleType(video);
  if (primaryKey) {
    sources = movePrimaryAngleToFront(sources, primaryKey);
  }

  return sources.map((source) => ({
    url: source.url,
    type: source.type,
    thumbnails_url: source.thumbnails_url,
    clip,
  }));
}

export function buildClippedItems(
  event: IEvent,
  angleType: IPixellotPlayerSourceType,
  getClipTitle?: (index: number, source: TVideoAngleSource) => string
): IVideoClippedListItem[] {
  const sources = getAnglesValue(event, angleType);

  return sources.map((source, index) => ({
    id: source.id ?? `clip-${index}`,
    url: source.url,
    poster: source.thumbnails_url ?? null,
    video_duration: source.duration ?? 0,
    type: "video-clipped-list-item" as const,
    title: getClipTitle?.(index, source) ?? `Clip ${index + 1}`,
    event: getEventFromAngleSource(event, source, angleType),
  }));
}

export function getClippedItems(
  event: IEvent,
  angleType?: IPixellotPlayerSourceType,
  getClipTitle?: (index: number, source: TVideoAngleSource) => string
): IVideoClippedListItem[] {
  if (!event || !isClippedVideo(event)) return [];

  const angle = angleType ?? getPrimaryAngleType(event);
  if (!angle) return [];

  return buildClippedItems(event, angle, getClipTitle);
}
